import React, { useMemo } from 'react';
import { Typography } from 'antd';
import { Line } from '@ant-design/plots';
import { buildLineChartData, LineMetricSeries } from './lineChartData';

const { Text } = Typography;

interface Props {
  title?: string;
  data?: LineMetricSeries[];
  fields?: string[];
  metricLabels?: Record<string, string>;
  metrics?: any[];  // All metrics for ID lookup
  areaNames?: Record<number, string>;
  showParent?: boolean;
  showChildren?: boolean;
  previewFallback?: boolean;
  fallbackMetricKey?: string;
  previewAreaId?: number;  // Area context for metric links
  onAreaClick?: (areaId: number) => void;
  onMetricClick?: (metricKey: string, metricId: number | undefined, areaId: number) => void;
}

const DEFAULT_PALETTE = ['#1d4ed8', '#0f766e', '#16a34a', '#f59e0b', '#ef4444', '#7c3aed'];

const toRgba = (hexColor: string, alpha: number) => {
  const hex = hexColor.replace('#', '').trim();
  if (hex.length === 3) {
    const r = parseInt(hex[0] + hex[0], 16);
    const g = parseInt(hex[1] + hex[1], 16);
    const b = parseInt(hex[2] + hex[2], 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  if (hex.length === 6) {
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return `rgba(22, 119, 255, ${alpha})`;
};

export const ReportLineChart: React.FC<Props> = ({
  title,
  data,
  fields,
  metricLabels,
  metrics,
  areaNames,
  showParent = true,
  showChildren = false,
  previewFallback,
  fallbackMetricKey,
  previewAreaId,
}) => {
  const { accentColor, palette } = useMemo(() => {
    const lineData = Array.isArray(data) ? data : [];
    const primaryMetricKey = lineData[0]?.metricKey || fallbackMetricKey;
    const metric = Array.isArray(metrics)
      ? metrics.find((entry) => entry?.key === primaryMetricKey)
      : undefined;
    const metricPalette = Array.isArray(metric?.colorPalette) ? metric.colorPalette.filter(Boolean) : [];
    const resolvedPalette = metricPalette.length > 0 ? metricPalette : DEFAULT_PALETTE;
    return {
      accentColor: resolvedPalette[0],
      palette: resolvedPalette,
    };
  }, [data, metrics, fallbackMetricKey]);

  const {
    displayPoints,
    unitLabel,
    xScaleType,
  } = useMemo(
    () =>
      buildLineChartData({
        data,
        fields,
        metricLabels,
        areaNames,
        showParent,
        showChildren,
        previewFallback,
        fallbackMetricKey,
      }),
    [data, fields, metricLabels, areaNames, showParent, showChildren, previewFallback, fallbackMetricKey]
  );

  const formatNumericValue = (raw: unknown) => {
    const num = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isFinite(num)) return null;
    const formatted = num.toFixed(2);
    return unitLabel ? `${formatted} ${unitLabel}` : formatted;
  };

  const config: any = {
    data: displayPoints.map(({ __seriesKey, __sortKey, __isParent, ...rest }) => rest),
    xField: 'x',
    yField: 'value',
    seriesField: 'series',
    colorField: 'series',
    color: palette,
    legend: { color: { position: 'top', title: 'Series' } },

    scale: {
      x: { type: xScaleType },
      y: { type: 'linear' },
    },

    smooth: false,
    height: 236,
    lineStyle: {
      lineWidth: 2.5,
    },
    point: {
      size: 4,
      shape: 'circle',
      style: {
        lineWidth: 2,
        stroke: '#ffffff',
      },
    },
    tooltip: {
      shared: true,
      showMarkers: true,
      items: [
        (datum: any) => ({
          name: String(datum?.series || 'Value'),
          value: formatNumericValue(datum?.value) ?? '—',
          color: datum?.color,
        }),
      ],
    },
    axis: {
      y: {
        title: unitLabel || false,
        grid: true,
        gridStroke: 'rgba(226, 232, 240, 0.9)',
        gridLineWidth: 1,
      },
      x: {
        title: false,
        labelAutoRotate: false,
        labelAutoHide: true,
      },
    },
    animation: false,
  };

  const areaLabel =
    previewAreaId && areaNames?.[previewAreaId]
      ? areaNames[previewAreaId]
      : undefined;

  return displayPoints.length > 0 ? (
    <div
      className="report-line-chart-surface"
      style={
        {
          '--report-chart-accent': accentColor,
          '--report-chart-accent-soft': toRgba(accentColor, 0.1),
        } as React.CSSProperties
      }
    >
      {title || areaLabel ? (
        <div className="report-line-chart-head">
          <div className="report-line-chart-title">{title || 'Trend'}</div>
          {areaLabel ? <div className="report-line-chart-area">{areaLabel}</div> : null}
        </div>
      ) : null}
      {unitLabel ? (
        <div className="report-line-chart-meta">
          <span className="report-line-chart-unit">{unitLabel}</span>
        </div>
      ) : null}
      <div
        className="report-line-chart-frame"
        style={{
          backgroundColor: toRgba(accentColor, 0.05),
          borderRadius: 10,
          padding: 8,
          border: `1px solid ${toRgba(accentColor, 0.12)}`,
        }}
      >
        <Line {...config} />
      </div>
    </div>
  ) : (
    <div className="report-line-chart-empty">
      <Text type="secondary">No data</Text>
    </div>
  );
};

export default ReportLineChart;
